import React from 'react';

// UI Components - shadcn/ui wrappers
const hasPaddingOverride = (className: string, axis: 'x' | 'y') => {
  const pattern =
    axis === 'x'
      ? /(^|\s)(?:[a-z]+:)*!?(?:p|px|pl|pr)-\S+/
      : /(^|\s)(?:[a-z]+:)*!?(?:p|py|pt|pb)-\S+/;

  return pattern.test(className);
};

export const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-[0_10px_35px_-18px_rgba(15,23,42,0.35)] ${className}`}>{children}</div>
);

export const CardHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => {
  const xPadding = hasPaddingOverride(className, 'x') ? '' : 'px-4 sm:px-6';
  const yPadding = hasPaddingOverride(className, 'y') ? '' : 'py-3 sm:py-4';

  return <div className={`border-b ${xPadding} ${yPadding} ${className}`}>{children}</div>;
};

export const CardContent: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => {
  const xPadding = hasPaddingOverride(className, 'x') ? '' : 'px-4 sm:px-6';
  const yPadding = hasPaddingOverride(className, 'y') ? '' : 'py-4 sm:py-5';

  return <div className={`${xPadding} ${yPadding} ${className}`}>{children}</div>;
};

export const CardTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => <h3 className={`text-base font-semibold text-slate-900 sm:text-lg ${className}`}>{children}</h3>;

export const Badge: React.FC<{ children: React.ReactNode; variant?: string; className?: string }> = ({
  children,
  variant = 'default',
  className = '',
}) => {
  const variants = {
    default: 'bg-blue-100 text-blue-800',
    outline: 'border border-slate-300 bg-white text-slate-700',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    destructive: 'bg-red-100 text-red-800',
    secondary: 'bg-gray-100 text-gray-800',
  };

  return (
    <span
      className={`inline-flex items-center whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium ${
        variants[variant as keyof typeof variants] || variants.default
      } ${className}`}
    >
      {children}
    </span>
  );
};

export const Button: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: string;
  size?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}> = ({ children, onClick, className = '', variant = 'default', size = 'md', type = 'button', disabled = false }) => {
  const variants = {
    default: 'bg-blue-600 text-white hover:bg-blue-700',
    outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
    destructive: 'bg-red-600 text-white hover:bg-red-700',
    ghost: 'hover:bg-gray-100',
  };

  const sizes = {
    sm: 'min-h-[2.25rem] px-3 py-2 text-sm',
    md: 'min-h-[2.75rem] px-4 py-2.5 text-sm sm:text-base',
    lg: 'min-h-[3rem] px-6 py-3 text-base sm:text-lg',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex max-w-full items-center justify-center gap-2 rounded-xl font-medium text-center leading-5 transition ${
        variants[variant as keyof typeof variants] || variants.default
      } ${sizes[size as keyof typeof sizes] || sizes.md} ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({
  type = 'text',
  placeholder = '',
  value = '',
  onChange,
  className = '',
  required = false,
  readOnly = false,
  disabled = false,
  autoComplete,
  ...props
}) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    required={required}
    readOnly={readOnly}
    disabled={disabled}
    autoComplete={autoComplete}
    {...props}
    className={`min-h-[2.75rem] w-full min-w-0 rounded-xl border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-base ${readOnly ? 'bg-gray-50' : ''} ${className}`}
  />
);

export const Alert: React.FC<{ children: React.ReactNode; variant?: string }> = ({
  children,
  variant = 'default',
}) => {
  const variants = {
    default: 'bg-blue-50 border-l-4 border-blue-500 text-blue-700',
    destructive: 'bg-red-50 border-l-4 border-red-500 text-red-700',
    success: 'bg-green-50 border-l-4 border-green-500 text-green-700',
  };

  return (
    <div className={`p-4 ${variants[variant as keyof typeof variants] || variants.default}`}>
      {children}
    </div>
  );
};

export const AlertDescription: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-sm">{children}</p>
);

export const Tabs: React.FC<{
  tabs: Array<{ label: string; content: React.ReactNode }>;
}> = ({ tabs }) => {
  const [activeTab, setActiveTab] = React.useState(0);

  return (
    <div>
      <div className="flex gap-2 overflow-x-auto border-b border-slate-200 pb-2">
        {tabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className={`shrink-0 rounded-xl px-4 py-2.5 text-sm font-medium transition sm:text-base ${
              activeTab === idx
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-slate-100 text-gray-600 hover:bg-slate-200 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="mt-4">{tabs[activeTab]?.content}</div>
    </div>
  );
};

export const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidthClass?: string;
}> = ({ isOpen, onClose, title, children, maxWidthClass = 'max-w-2xl' }) => {
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/55 px-3 py-3 sm:p-4">
      <div className="flex min-h-full items-end justify-center sm:items-center">
        <div
          className={`w-full overflow-hidden rounded-[1.75rem] bg-white shadow-xl sm:max-h-[90vh] sm:rounded-2xl ${maxWidthClass}`}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
            <h2 className="pr-4 text-base font-semibold break-words text-slate-900 sm:text-xl">{title}</h2>
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-gray-500 transition hover:bg-slate-100 hover:text-gray-700"
            >
            ✕
            </button>
          </div>
          <div className="max-h-[calc(100dvh-6rem)] overflow-y-auto px-4 py-4 break-words sm:max-h-[calc(90vh-73px)] sm:px-6 sm:py-5">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Label: React.FC<{ children: React.ReactNode; className?: string; htmlFor?: string }> = ({
  children,
  className = '',
  htmlFor,
}) => (
  <label htmlFor={htmlFor} className={`block text-sm font-medium text-gray-700 ${className}`}>
    {children}
  </label>
);

export const Switch: React.FC<{
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}> = ({ checked = false, onCheckedChange, disabled = false, className = '' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onCheckedChange?.(!checked)}
    className={`
      relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
      transition-colors duration-200 ease-in-out
      focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
      ${checked ? 'bg-blue-600' : 'bg-gray-200'}
      ${disabled ? 'cursor-not-allowed opacity-50' : ''}
      ${className}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
        transition duration-200 ease-in-out
        ${checked ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);
